import { useEffect, useState } from "react";
import { Plus, Truck, User, Pencil, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, StatusBadge, EmptyState, Field } from "../components/Shared";
import { api, fmtDate, formatApiError } from "../lib/api";
import { RESOURCE_STATUS, STAFF_ROLES, VEHICLE_TYPES, canWrite } from "../lib/labels";
import { useAuth } from "../context/AuthContext";

const CONFIG = {
  vehicles: {
    empty: { name: "", plate: "", type: "camion", capacity_m3: 20, status: "disponible", next_maintenance: "" },
    statuses: ["disponible", "en_mission", "maintenance"],
    title: "véhicule",
  },
  staff: {
    empty: { name: "", role: "porteur", phone: "", status: "disponible", user_id: "" },
    statuses: ["disponible", "en_mission", "conge"],
    title: "membre d'équipe",
  },
};

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

function ResourceDialog({ kind, initial, onClose, onSaved }) {
  const cfg = CONFIG[kind];
  const [form, setForm] = useState(cfg.empty);
  useEffect(() => {
    setForm({ ...cfg.empty, ...(initial || {}) });
  }, [initial, cfg]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? (k === "capacity_m3" ? Number(e.target.value) : e.target.value) : e });
  const statusOpts = Object.fromEntries(cfg.statuses.map((s) => [s, RESOURCE_STATUS[s].label]));
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (kind === "staff") api.get("/team").then((r) => setAccounts(r.data.users)).catch(() => {});
  }, [kind]);
  const accountOpts = { none: "Aucun compte", ...Object.fromEntries(accounts.map((a) => [a.id, `${a.name} (${a.email})`])) };
  const save = async () => {
    try {
      const { id, company_id, created_at, ...p } = form;
      if (id) await api.put(`/${kind}/${id}`, p);
      else await api.post(`/${kind}`, p);
      toast.success("Enregistré");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{form.id ? "Modifier" : "Ajouter"} un {cfg.title}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Nom"><Input data-testid={`${kind}-name-input`} value={form.name} onChange={set("name")} /></Field></div>
          {kind === "vehicles" ? (
            <>
              <Field label="Immatriculation"><Input data-testid="vehicles-plate-input" value={form.plate} onChange={set("plate")} /></Field>
              <SelectField label="Type" value={form.type} onChange={set("type")} options={VEHICLE_TYPES} testid="vehicles-type-select" />
              <Field label="Capacité (m³)"><Input data-testid="vehicles-capacity-input" type="number" min="0" value={form.capacity_m3} onChange={set("capacity_m3")} /></Field>
              <Field label="Prochain entretien"><Input data-testid="vehicles-maintenance-input" type="date" value={form.next_maintenance} onChange={set("next_maintenance")} /></Field>
            </>
          ) : (
            <>
              <SelectField label="Poste" value={form.role} onChange={set("role")} options={STAFF_ROLES} testid="staff-role-select" />
              <Field label="Téléphone"><Input data-testid="staff-phone-input" value={form.phone} onChange={set("phone")} /></Field>
              <div className="sm:col-span-2">
                <SelectField label="Compte Nomade lié (itinéraire du jour)" value={form.user_id || "none"} onChange={(v) => setForm({ ...form, user_id: v === "none" ? "" : v })} options={accountOpts} testid="staff-user-select" />
              </div>
            </>
          )}
          <SelectField label="Statut" value={form.status} onChange={set("status")} options={statusOpts} testid={`${kind}-status-select`} />
        </div>
        <DialogFooter><Button data-testid={`${kind}-save-button`} className="bg-[#0b2c6b]" onClick={save}>Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResourceCard({ item, kind, onEdit, onDelete, readOnly }) {
  const Icon = kind === "vehicles" ? Truck : User;
  return (
    <div className="card-hover rounded-xl border border-slate-200 bg-white p-5" data-testid={`${kind}-card-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-[#0b2c6b]"><Icon className="h-5 w-5" /></div>
        <StatusBadge map={RESOURCE_STATUS} value={item.status} />
      </div>
      <p className="mt-4 font-semibold text-slate-900">{item.name}</p>
      <p className="text-sm text-slate-500">
        {kind === "vehicles" ? `${VEHICLE_TYPES[item.type] || item.type} · ${item.plate} · ${item.capacity_m3} m³` : `${STAFF_ROLES[item.role] || item.role} · ${item.phone || "—"}`}
      </p>
      {kind === "vehicles" && item.next_maintenance && <p className="mt-2 flex items-center gap-1 text-xs text-slate-500"><Wrench className="h-3 w-3" />Entretien : {fmtDate(item.next_maintenance)}</p>}
      {!readOnly && (
        <div className="mt-3 flex gap-1">
          <Button size="sm" variant="ghost" data-testid={`edit-${kind}-${item.id}`} onClick={() => onEdit(item)}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" data-testid={`delete-${kind}-${item.id}`} onClick={() => onDelete(item)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      )}
    </div>
  );
}

export default function Fleet() {
  const [data, setData] = useState({ vehicles: [], staff: [] });
  const [tab, setTab] = useState("vehicles");
  const [editing, setEditing] = useState(undefined);
  const { user } = useAuth();
  const editable = canWrite(user, "fleet");

  const load = () => Promise.all([api.get("/vehicles"), api.get("/staff")]).then(([v, s]) => setData({ vehicles: v.data, staff: s.data }));
  useEffect(() => {
    load();
  }, []);
  const remove = async (item) => {
    if (!window.confirm(`Supprimer ${item.name} ?`)) return;
    await api.delete(`/${tab}/${item.id}`);
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Ressources" title="Équipes & véhicules" subtitle="Flotte, entretien et disponibilité des déménageurs.">
        {editable && <Button data-testid="add-resource-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]" onClick={() => setEditing(null)}><Plus className="mr-2 h-4 w-4" />Ajouter</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5">
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Véhicules ({data.vehicles.length})</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Équipe ({data.staff.length})</TabsTrigger>
        </TabsList>
        {["vehicles", "staff"].map((k) => (
          <TabsContent key={k} value={k}>
            {data[k].length === 0 ? <EmptyState text="Aucun élément" testid={`${k}-empty`} /> : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {data[k].map((it) => <ResourceCard key={it.id} item={it} kind={k} onEdit={setEditing} onDelete={remove} readOnly={!editable} />)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
      {editing !== undefined && <ResourceDialog kind={tab} initial={editing} onClose={() => setEditing(undefined)} onSaved={load} />}
    </div>
  );
}
